import React, { useEffect, useState } from 'react';
// eslint-disable-next-line import/no-extraneous-dependencies
import { IHeaderParams } from '@ag-grid-community/all-modules';
import ServerSideGridGroupHeaderCollapseExtandIcon from './ServerSideGridGroupHeaderCollapseExtandIcon';
import ServerSideGridGroupHeaderSortIcon from './ServerSideGridGroupHeaderSortIcon';
import ServerSideGridGroupHeaderMenuIcon from './ServerSideGridGroupHeaderMenuIcon';
import './server-side-grid-group-header.scss';

const SORT_NEXT_CONFIGURATION: { [key: string]: string } = {
  none: 'asc',
  asc: 'desc',
  desc: '',
};

type Props = Partial<IHeaderParams>;

export default function ServerSideGridGroupHeader({
  displayName,
  column,
  api,
  enableMenu,
  enableSorting,
  setSort,
  showColumnMenu,
}: Props): JSX.Element {
  const [expandAll, setExpandAll] = useState(false);
  const [sortOrder, setSortOrder] = useState('none');

  useEffect(() => {
    const sortChangedListener = () => setSortOrder(column?.getSort() ?? 'none');
    column?.addEventListener('sortChanged', sortChangedListener);
    return () => column?.removeEventListener('sortChanged', sortChangedListener);
  }, [column]);

  const handleClick = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
    if (!enableSorting) {
      return;
    }

    const newSort = SORT_NEXT_CONFIGURATION[column?.getSort() ?? 'none'];
    setSort?.(newSort as ('asc' | 'desc'), event.shiftKey);
  };

  const handleExpandCollapseClick = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
    event.stopPropagation();

    const newAllCollapsed = !expandAll;
    if (api?.forEachNode) {
      api?.forEachNode((node) => {
        if (node.level === 0) {
          node.setExpanded(newAllCollapsed);
        }
      });
    }

    setExpandAll(newAllCollapsed);
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
    event.stopPropagation();
    showColumnMenu?.(event.currentTarget);
  };

  return (
    <div className="server-side-grid-group-header" onClick={handleClick} role="presentation">
      <div className="ag-header-cell-label">
        <ServerSideGridGroupHeaderCollapseExtandIcon onClick={handleExpandCollapseClick} collapsed={!expandAll} />
        <div className="ag-header-cell-label" role="presentation" unselectable="on">
          <span className="ag-header-cell-text" role="columnheader" unselectable="on">{displayName}</span>
          <ServerSideGridGroupHeaderSortIcon sort={sortOrder} />
        </div>
      </div>
      {enableMenu && <ServerSideGridGroupHeaderMenuIcon onClick={handleMenuClick} />}
    </div>
  );
}
